import React, { useEffect, useRef, useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import Footer from "./footer";
import Header from "./header";
import {
  vehroutelist,
  vehtriplist,
  vehicleLocationUpdate,
  workerdetail,
  workerdashboardcount,
} from "../service/dashboard";
import moment from "moment";
import { toast } from "react-toastify";

// Minimum gap between location pushes to the server (ms).
const SEND_INTERVAL = 12000;

const ShareLocation = () => {
  const [rlist, setRList] = useState([]);       // all routes (master)
  const [tlist, setTList] = useState([]);       // all trips (master)
  const [vehRoutes, setVehRoutes] = useState([]); // routes for the driver's vehicle
  const [vehTrips, setVehTrips] = useState([]);   // trips for the driver's vehicle
  const [vehRouteIds, setVehRouteIds] = useState(""); // comma ids from the vehicle
  const [vehTripIds, setVehTripIds] = useState("");

  const [form, setForm] = useState({ vehicle: "", route: "", trip: "" });

  const [sharing, setSharing] = useState(false);
  const [status, setStatus] = useState("");     // human readable status line
  const [lastFix, setLastFix] = useState(null); // { lat, lng, at }

  const watchId = useRef(null);
  const lastSent = useRef(0);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    const temp1 = new FormData();
    temp1.append("academy_id", localStorage.getItem("academy"));
    vehroutelist(temp1).then((res) => {
      if (res && res.status === true) setRList(res.data);
    });
    vehtriplist(temp1).then((res) => {
      if (res && res.status === true) setTList(res.data);
    });

    // The vehicle is fixed to the logged-in driver's own bus. Resolve the
    // driver's worker id, then read their assigned vehicle from the profile.
    const wtemp = new FormData();
    wtemp.append("user_id", localStorage.getItem("UID"));
    wtemp.append("role_id", localStorage.getItem("Role"));
    workerdashboardcount(wtemp).then((wres) => {
      const workerId = wres?.status === true && wres.data.workers ? wres.data.workers.id : null;
      if (!workerId) return;
      workerdetail(workerId).then((res) => {
        if (res?.status === true && res.data.transport) {
          const v = res.data.transport;
          setForm((prev) => ({ ...prev, vehicle: String(v.id) }));
          setVehRouteIds(String(v.vehicle_route || ""));
          setVehTripIds(String(v.vehicle_trip || ""));
        }
      });
    });

    // Stop the GPS watch if the driver navigates away / closes the screen.
    return () => stopWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scope the route/trip dropdowns to the vehicle's configured ids once the
  // master lists and the driver's vehicle are both loaded. Auto-select when
  // there is only one option.
  useEffect(() => {
    const routeIds = vehRouteIds.split(",").map((v) => v.trim()).filter((v) => v !== "");
    const tripIds = vehTripIds.split(",").map((v) => v.trim()).filter((v) => v !== "");
    const routes = rlist.filter((r) => routeIds.includes(String(r.id)));
    const trips = tlist.filter((t) => tripIds.includes(String(t.id)));
    setVehRoutes(routes);
    setVehTrips(trips);
    if (routes.length === 1) setForm((prev) => ({ ...prev, route: String(routes[0].id) }));
    if (trips.length === 1) setForm((prev) => ({ ...prev, trip: String(trips[0].id) }));
  }, [rlist, tlist, vehRouteIds, vehTripIds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  function sendLocation(coords) {
    const f = formRef.current;
    const temp = new FormData();
    temp.append("vehicle_id", f.vehicle);
    if (f.route) temp.append("route_id", f.route);
    if (f.trip) temp.append("trip_id", f.trip);
    temp.append("latitude", coords.latitude);
    temp.append("longitude", coords.longitude);
    if (coords.speed != null) temp.append("speed", coords.speed);
    if (coords.heading != null) temp.append("heading", coords.heading);
    if (coords.accuracy != null) temp.append("accuracy", coords.accuracy);
    temp.append("recorded_at", moment().format("YYYY-MM-DD HH:mm:ss"));

    vehicleLocationUpdate(temp).then((res) => {
      if (res && res.status === true) {
        setLastFix({ lat: coords.latitude, lng: coords.longitude, at: new Date() });
        setStatus("Live · sharing your location");
      } else if (res) {
        setStatus(res.message || "Server rejected the update");
      }
    });
  }

  function onPosition(pos) {
    const now = Date.now();
    if (now - lastSent.current < SEND_INTERVAL) return; // throttle
    lastSent.current = now;
    sendLocation(pos.coords);
  }

  function onError(err) {
    if (err.code === err.PERMISSION_DENIED) {
      setStatus("Location permission denied. Enable location access to share.");
      toast.error("Location permission denied");
      stopWatch();
    } else if (err.code === err.POSITION_UNAVAILABLE) {
      setStatus("Location unavailable. Check GPS / signal.");
    } else if (err.code === err.TIMEOUT) {
      setStatus("Getting a GPS fix…");
    }
  }

  function startWatch() {
    if (form.vehicle === "") { toast.error("Vehicle not loaded yet, please wait"); return; }
    if (form.route === "") { toast.error("Please Select Route"); return; }
    if (form.trip === "") { toast.error("Please Select Trip"); return; }

    if (!("geolocation" in navigator)) {
      toast.error("Geolocation is not supported on this device");
      return;
    }

    lastSent.current = 0; // send the first fix immediately
    setStatus("Starting… waiting for GPS");
    watchId.current = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 20000,
    });
    setSharing(true);
  }

  function stopWatch() {
    if (watchId.current != null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setSharing(false);
  }

  function handleStop() {
    stopWatch();
    setStatus("Stopped sharing");
    toast.success("Location sharing stopped");
  }

  return (
    <div id="mainpage">
      <div className="maincontent">
        <Header />

        <div className="moreheading">
          <Row className="row-sm align-items-center">
            <Col xs={12} sm={6} className="text-left mb-2 mb-sm-0">
              <h4 className="morehead-text mb-0">Share Bus Location</h4>
            </Col>
            <Col xs={12} sm={6}>
              <div className="d-flex flex-wrap justify-content-start justify-content-sm-end gap-2">
                <Link to={"/dashboard"} className="btn-outline-success red-btn btn-sm">
                  <i className="fa-solid fa-hand-point-left"></i> Go Back
                </Link>
              </div>
            </Col>
          </Row>
        </div>

        <div className="card-body">
          <form>
            <div className="mb-3">
              <label className="form-label attachment">Route <span>*</span></label>
              <select
                className="form-select"
                name="route"
                value={form.route}
                onChange={handleChange}
                disabled={sharing}
                required
              >
                <option value="">Select Route</option>
                {vehRoutes.map((r) => (
                  <option value={r.id} key={r.id}>{r.vehroute_name}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label attachment">Trip <span>*</span></label>
              <select
                className="form-select"
                name="trip"
                value={form.trip}
                onChange={handleChange}
                disabled={sharing}
                required
              >
                <option value="">Select Trip</option>
                {vehTrips.map((t) => (
                  <option value={t.id} key={t.id}>{t.vehtrip_name}</option>
                ))}
              </select>
            </div>

            <div className="text-center">
              {!sharing ? (
                <button type="button" className="btn btn-success px-5" onClick={startWatch}>
                  <i className="fa-solid fa-location-dot me-2"></i> Start Trip &amp; Share
                </button>
              ) : (
                <button type="button" className="btn btn-danger px-5" onClick={handleStop}>
                  <i className="fa-solid fa-stop me-2"></i> Stop Sharing
                </button>
              )}
            </div>
          </form>

          {(status || sharing) && (
            <div className="text-center mt-4">
              <div className={`fw-bold ${sharing ? "text-success" : "text-muted"}`}>
                {sharing && <span className="spinner-grow spinner-grow-sm text-success me-2" role="status" />}
                {status}
              </div>
              {lastFix && (
                <div className="small text-muted mt-2">
                  Last sent: {lastFix.lat.toFixed(5)}, {lastFix.lng.toFixed(5)}
                  <br />
                  at {moment(lastFix.at).format("hh:mm:ss A")}
                </div>
              )}
              {sharing && (
                <div className="small text-muted mt-2">
                  Keep this screen open during the trip.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ShareLocation;
